// external
import { useCallback, useEffect, useContext } from "react";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { ClientContext } from "context/ClientContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { FaoResultsContext } from "context/FaoResultsContext";

/**
 * useNonModeledData Hook
 *
 * This hook manages the loading and initialization of non-modeled data for FAO models.
 * It handles the complete data lifecycle including:
 *
 * Data Sources:
 * - Base tuition data from previous academic year
 * - Cost of attendance (COA) data
 * - Non-modeled population data from the model
 * - Existing scenario adjustments (if any)
 *
 * Key Responsibilities:
 * - Fetches base data from multiple API endpoints in parallel
 * - Loads existing scenario adjustments if they exist
 * - Manages loading states to prevent UI flashing
 * - Initializes reducers with base and adjusted data
 *
 * The hook follows a specific loading sequence:
 * 1. Load base data (tuition, COA, non-model population)
 * 2. Initialize base state in reducers
 * 3. Load existing adjustments or use base values
 * 4. Update loading state when complete
 */
function useNonModeledData() {
  const { currentClientData } = useContext(ClientContext);
  const { modelConfig, getExistingConfig } = useContext(FaoBuilderContext);
  const {
    costOfAttendanceDispatch,
    nonModeledData,
    nonModeledDispatch,
    loading,
    setLoading,
    isSaved,
    setIsSaved,
  } = useContext(FaoResultsContext);

  /**
   * Reset saved state whenever non-modeled data changes
   * This ensures the save button reflects the current state
   */
  useEffect(() => {
    setIsSaved({ ...isSaved, nonModeledData: false });
  }, [nonModeledData]);

  /**
   * Loads existing scenario data if available
   * This includes adjusted tuition, COA, and non-modeled populations
   *
   * @param {string} existingId - The ID of the existing scenario
   * @param {Array} baseExclusionValue - Fallback data if loading fails
   */
  const loadExistingData = useCallback(
    async (existingId, baseExclusionValue) => {
      try {
        // Fetch existing scenario configuration
        const data = await getExistingConfig({
          configType: "scenario",
          endpoint: "/api/fao-scenarios",
          params: {
            id: existingId,
            schoolKey: currentClientData.schoolKey,
          },
        });

        // Update cost of attendance with adjusted values
        costOfAttendanceDispatch({
          type: "update_current_year",
          tuition: data.adjustedTuition,
          coa: data.adjustedCoa,
        });

        // Update non-modeled data with existing adjustments
        nonModeledDispatch({
          type: "update_adjusted",
          exclusionGroups: data.adjustedNonmodel || baseExclusionValue,
        });
      } catch (error) {
        console.error("Error loading scenario data:", error);
        // Reset to base state if loading fails
        nonModeledDispatch({ type: "reset_adjusted" });
      }
    },
    [currentClientData.schoolKey, getExistingConfig, costOfAttendanceDispatch]
  );

  /**
   * Main data loading effect
   * Orchestrates the complete data loading sequence when component mounts
   *
   * Loading sequence:
   * 1. Set loading state to prevent UI flashing
   * 2. Fetch base data in parallel (tuition, COA, non-model populations)
   * 3. Initialize base state in reducers
   * 4. Load existing adjustments or use base values
   * 5. Clear loading state
   */
  useEffect(() => {
    async function handleInitialLoad() {
      try {
        // Set loading state using functional update to prevent stale closure issues
        setLoading((prev) => ({ ...prev, nonModeledData: true }));

        // Fetch all base data in parallel for better performance
        const [tuitionRes, coaRes, nonmodelPopRes] = await Promise.all([
          // Get previous year's tuition data
          apiRoot.get("/api/fao-scenarios/tuition", {
            params: {
              schoolKey: currentClientData.schoolKey,
              entryTerm: modelConfig.entryTerm,
            },
          }),
          // Get previous year's cost of attendance
          apiRoot.get("/api/fao-scenarios/coa", {
            params: {
              schoolKey: currentClientData.schoolKey,
              entryTerm: modelConfig.entryTerm,
            },
          }),
          // Get non-modeled population data for this model
          apiRoot.get("/api/fao-scenarios/nonmodel", {
            params: { modelId: modelConfig.id },
          }),
        ]);

        // Update cost of attendance reducer with last year's data
        costOfAttendanceDispatch({
          type: "update_last_year",
          tuition:
            tuitionRes.data[0].Academic_Year_Tuition_Fees || "No Base Value",
          coa: coaRes.data[0].COA || "No Base Value",
        });

        const baseExclusionValue = nonmodelPopRes.data;

        // Initialize the non-modeled data reducer with base values
        nonModeledDispatch({
          type: "initialize",
          exclusionGroups: baseExclusionValue,
        });

        // Check if there's an existing scenario to load
        const existingId = modelConfig.configs["scenario"];
        if (existingId) {
          // Load existing adjustments
          await loadExistingData(existingId, baseExclusionValue);
        } else {
          // No existing scenario - use base values as adjusted values
          nonModeledDispatch({
            type: "update_adjusted",
            exclusionGroups: baseExclusionValue,
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
        // Continue even if there's an error - let the UI handle empty states
      } finally {
        // Always clear loading state, even if there was an error
        setLoading((prev) => ({ ...prev, nonModeledData: false }));
      }
    }

    // Only run the effect once when the component mounts
    // Dependencies are omitted intentionally to prevent re-runs
    handleInitialLoad();
  }, []);
}

export default useNonModeledData;
